import * as yup from 'yup';

import { MAX_FILE_SIZE_ALLOWED } from '~/config/constants';
import { CARD_NAME_REGEX } from '~/config/constants';
import { squish } from '~/utils/helpers';
import checkDuplicateNameApi from '~/api/checkDuplicateName.api';

yup.setLocale({
    mixed: {
        required: '* required.',
    },
    array: {
        min: function ({ min }) {
            if (min === 1) return '* required.';

            return `must be at least ${min} items.`;
        },
    },
});

//** by leaving `ref` outside `checkDuplicateName`, I can cache its value for any upcoming validation so that checkDuplicateNameApi() will not be run every time this func get called.
let ref;
let error;
export const checkDuplicateName = (initialName, apiPrefix) => async (val) => {
    const value = val?.toLowerCase();
    if (!value) {
        return;
    }

    if (squish(value.toLowerCase()) === squish(initialName?.toLowerCase())) {
        return;
    }

    if (ref === value) return error;

    ref = value;

    const status = await checkDuplicateNameApi(value, apiPrefix.split('/').reverse()[0]);
    error = !status ? 'same name exists' : null;

    return error;
};

export const cardNameValidator = () =>
    stringValidator.required().test('checkValidName', 'special character not allowed', function (value) {
        if (!value) return true;

        const text = value.replace(/\s/g, '');
        const result = CARD_NAME_REGEX.test(text);

        return !result;
    });

export const statusValidator = yup
    .array()
    .of(
        yup.object().shape({
            status: yup.string().nullable(), // these constraints take precedence
            statusVal: yup.number().min(0, ' must be > 0').nullable(), // these constraints take precedence
        }),
    )
    .required();

export const ckEditorValidator = (max) =>
    yup
        .mixed()
        .required()
        .test('description', '* required.', (value, testContext) => {
            let text = '';
            value.blocks.forEach((block) => {
                text += block.text || '';
            });

            if (text.length === 0) return false;

            if (text.length > max) {
                return testContext.createError({
                    path: '',
                    params: { max },
                    message: 'max character allowed is ${max}',
                });
            }

            return true;
        });

export const imageValidator = (nullable, maxSize = null) =>
    yup
        .mixed()
        .when([], {
            is: () => !nullable,
            then: yup.mixed().required(),
            otherwise: yup.mixed().notRequired(),
        }) //https://dev.to/atosh502/conditional-validation-of-form-fields-using-yup-393j
        .test('fileType', 'incorrect file type.', (file) => {
            if (typeof file === 'string' || typeof file === 'undefined') return true;

            // need to return true or false, else `required()` will not work
            return file && ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'].includes(file.type);
        })
        .test('fileSize', `file size must be <= ${maxSize || MAX_FILE_SIZE_ALLOWED} bytes.`, (file) => {
            if (typeof file === 'string' || typeof file === 'undefined') return true;
            const sizeAllowed = maxSize || MAX_FILE_SIZE_ALLOWED;

            return file && file.size <= sizeAllowed;
        });

export const conditionValueValidator = (dependentField) =>
    yup
        .number()
        .required()
        .when(dependentField, (dependentField, schema) =>
            dependentField?.length === 0
                ? schema.oneOf([0], 'must be 0 as there is no condition check.')
                : schema.positive('must be a positive number'),
        ); //https://stackoverflow.com/a/69003630/11297747

export const integerPositiveValidator = yup
    .number()
    .typeError('incorrect number format.')
    .min(0, 'must be >= 0.')
    .required();
export const integerNegativeAllowedValidator = yup.number().typeError('incorrect number format.').required();

export const booleanValidator = yup.boolean().required();

export const stringValidator = yup.string();

export const arrayValidator = yup.array().required();

export const enumValidator = (enums) => {
    let ids = enums.map((item) => item.id);
    return yup
        .mixed()
        .required()
        .oneOf([...ids]);
};

export default yup;
/** Note*/
//(1) asyncDebounce() only works if `validateOnChange` is set to false at <Formik>, which I already did as it is better for the duplName validator to be triggered only on blur to avoid  unnecessary api call.
